/// <reference types="vite/client" />
// Every language's words at run time.
//
// English is written in the phrase tables (phrases/*.ts, kioskText.ts, book/words.ts).
// Each other language is one file, locales/<code>.json (flat key → text), built
// into its own chunk and fetched from this server the first time it is needed.
// An organisation's own words (Settings › Wording) sit on top.
//
// Lookup: organisation(lang) → shipped(lang) → organisation(en) → English.
// A key is never shown: with no English either, the answer is '' (warned in dev).
import { SOURCE_LANGUAGE } from './languages'
import { formatPhrase, type Vars } from './plural'

/** An organisation's words: lang → key → text. */
export type Wording = Record<string, Record<string, string>>

type Table = Record<string, string>

let loaders: Record<string, () => Promise<Table>> = {}
try {
  loaders = import.meta.glob<Table>('./locales/*.json', { import: 'default' })
} catch {
  // Outside Vite (the Playwright suite imports the phrase tables): English only.
}
const loaded = new Map<string, Table>()
const pending = new Map<string, Promise<void>>()
let wording: Wording = {}
let version = 0
const listeners = new Set<() => void>()

function changed(): void {
  version++
  for (const listener of listeners) listener()
}

/** For useSyncExternalStore: re-render when a language arrives or the wording changes. */
export function subscribeWords(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function wordsVersion(): number {
  return version
}

/** The languages this build ships a file for (English is the source and has none). */
export function shippedLanguages(): string[] {
  return Object.keys(loaders).map((path) => path.replace(/^\.\/locales\/|\.json$/g, ''))
}

export function isLanguageLoaded(code: string): boolean {
  return code === SOURCE_LANGUAGE || loaded.has(code) || !(`./locales/${code}.json` in loaders)
}

/** Fetch a language's file once. A failure leaves English showing; it is asked again next time. */
export function loadLanguage(code: string): Promise<void> {
  if (isLanguageLoaded(code)) return Promise.resolve()
  const running = pending.get(code)
  if (running) return running
  const load = loaders[`./locales/${code}.json`]!()
    .then((table) => {
      loaded.set(code, table)
      changed()
    })
    .catch(() => undefined)
    .finally(() => pending.delete(code))
  pending.set(code, load)
  return load
}

/** Put a language's words in place directly (tests, and a screen that already holds them). */
export function registerLanguage(code: string, table: Table): void {
  loaded.set(code, table)
  changed()
}

/** The organisation's words, as identity or a screen's design answer carries them. */
export function setWording(next: Wording | null | undefined): void {
  const clean: Wording = {}
  for (const [lang, words] of Object.entries(next ?? {})) {
    if (words && typeof words === 'object') clean[lang] = { ...words }
  }
  if (JSON.stringify(clean) === JSON.stringify(wording)) return
  wording = clean
  changed()
}

export function currentWording(): Wording {
  return wording
}

/** The shipped word in lang, before any organisation's; undefined when the file has none. */
export function shippedWord(key: string, lang: string): string | undefined {
  if (lang === SOURCE_LANGUAGE) return undefined
  const text = loaded.get(lang)?.[key]
  return text && text.trim() ? text : undefined
}

const own = (lang: string, key: string): string | undefined => {
  const text = wording[lang]?.[key]
  return text && text.trim() ? text : undefined
}

/**
 * The text of key in lang, before plurals and placeholders.
 * legacy is a translation still written beside the English in a TS table (dormant az/ru),
 * read only when the language file has none.
 */
export function wordText(key: string, lang: string, english: string | undefined, legacy?: string): string {
  const text =
    own(lang, key) ??
    shippedWord(key, lang) ??
    (legacy && legacy.trim() ? legacy : undefined) ??
    own(SOURCE_LANGUAGE, key) ??
    english
  if (text) return text
  if (import.meta.env?.DEV && english !== '') console.warn(`[i18n] no English for ${key}`)
  return ''
}

/** wordText, said: plural forms chosen and placeholders filled. */
export function sayWord(key: string, lang: string, english: string | undefined, vars?: Vars, legacy?: string): string {
  return formatPhrase(wordText(key, lang, english, legacy), lang, vars)
}
